import debounce from '@/mixins/debounce';
import errorHandler from '@/mixins/errorHandler';
import {mapGetters, mapMutations} from 'vuex';
import uniqKey from '@/mixins/uniqKey';

export default {
    mixins: [debounce, errorHandler, uniqKey],

    data() {
        return {
            caseId: this.$route.params.slug,
            page: 1,
            pageSize: 10,
            keySearch: '',
            tagsList: [],
            galleryTagsList: [],
            displayedMediaList: [],
            comments: [],
            chatTags: [],
        };
    },
    computed: {
        ...mapGetters('mediaTagging', {
            clientId: 'getClientId',
            deviceInfo: 'getDeviceInfo',
            clientInfo: 'getClientInfo',
            taggingItem: 'getTaggingItem',
        })
    },
    methods: {
        ...mapMutations('mediaTagging', {
            setLoadingState: 'SET_LOADING_STATE',
            setTaggingItem: 'SET_TAGGING_ITEM'
        }),

        getMediaByGalleryId(galleryId = null) {
            this.setLoadingState(true);
            this.$load(async () => {
                    const res = await this.$api.media.getGalleryById(
                        this.caseId,
                        this.clientId,
                        galleryId || this.taggingItem.id
                    );
                    this.setTaggingItem(res.data.body);
                    const index = this.displayedMediaList
                        .findIndex(item => item.id === (galleryId || this.taggingItem.id));
                    if (index !== -1) {
                        this.$set(this.displayedMediaList, index, res.data.body);
                    }
                    this.debounce(() => this.setLoadingState(false), 1000);
                },
                error => {
                    this.debounce(() => this.setLoadingState(false), 1000);
                    this.handleResponseError(error);
                }
            );
        },

        // EDIT TAGS POPUP
        getListTags() {
            this.setLoadingState(true);
            this.$load(
                async () => {
                    const res = await this.$api.media.getListTags(this.caseId);
                    this.tagsList = (res.data.body || []).map(category => ({
                        ...category,
                        tags: (category.tags || []).map(tag => ({
                            ...tag,
                        })),
                    }));
                    this.debounce(() => this.setLoadingState(false), 1000);
                },
                error => {
                    this.debounce(() => this.setLoadingState(false), 1000);
                    this.handleResponseError(error);
                }
            );
        },

        async addNewTag(categoryId, tagData) {
            this.setLoadingState(true);
            await this.$load(
                async () => {
                    await this.$api.media.addNewTag(
                        this.caseId,
                        categoryId,
                        tagData
                    );
                    await this.getListTags();
                    await this.getListTagsForGallery();
                    this.debounce(() => this.setLoadingState(false), 1000);
                },
                error => {
                    this.debounce(() => this.setLoadingState(false), 1000);
                    this.handleResponseError(error);
                }
            );
        },

        async editTag(tagId, tagData) {
            this.setLoadingState(true);
            await this.$load(
                async () => {
                    await this.$api.media.editTag(
                        this.caseId,
                        tagId,
                        tagData
                    );
                    await this.getListTags();
                    await this.getListTagsForGallery();
                    this.debounce(() => this.setLoadingState(false), 1000);
                },
                error => {
                    this.debounce(() => this.setLoadingState(false), 1000);
                    this.handleResponseError(error);
                }
            );
        },

        async removeTag(tagId) {
            this.setLoadingState(true);
            await this.$load(
                async () => {
                    await this.$api.media.removeTag(
                        this.caseId,
                        tagId
                    );
                    await this.getListTags();
                    await this.getListTagsForGallery();
                    this.debounce(() => this.setLoadingState(false), 1000);
                },
                () => {
                    this.debounce(() => this.setLoadingState(false), 1000);
                    throw new Error('');
                }
            );
        },

        // CATEGORIES
        async addNewCategory(categoryData) {
            this.setLoadingState(true);
            await this.$load(
                async () => {
                    await this.$api.media.addNewCategory(
                        this.caseId,
                        categoryData
                    );
                    await this.getListTagsForGallery();
                    await this.getListTags();
                    this.debounce(() => this.setLoadingState(false), 1000);
                },
                error => {
                    this.debounce(() => this.setLoadingState(false), 1000);
                    this.handleResponseError(error);
                }
            );
        },

        async editCategory(categoryId, categoryData) {
            this.setLoadingState(true);
            await this.$load(
                async () => {
                    await this.$api.media.editCategory(
                        this.caseId,
                        categoryId,
                        categoryData
                    );
                    await this.getListTags();
                    await this.getListTagsForGallery();
                    this.debounce(() => this.setLoadingState(false), 1000);
                },
                error => {
                    this.debounce(() => this.setLoadingState(false), 1000);
                    this.handleResponseError(error);
                }
            );
        },

        async removeCategory(categoryId) {
            this.setLoadingState(true);
            await this.$load(
                async () => {
                    await this.$api.media.removeCategory(
                        this.caseId,
                        categoryId
                    );
                    await this.getListTags();
                    await this.getListTagsForGallery();
                    this.debounce(() => this.setLoadingState(false), 1000);
                },
                error => {
                    this.debounce(() => this.setLoadingState(false), 1000);
                    this.handleResponseError(error);
                }
            );
        },

        async changeTagOrder(orderData) {
            this.setLoadingState(true);
            await this.$load(
                async () => {
                    await this.$api.media.changeTagOrder(
                        this.caseId,
                        orderData
                    );
                    await this.getListTags();
                    await this.getListTagsForGallery();
                    this.debounce(() => this.setLoadingState(false), 1000);
                },
                error => {
                    this.debounce(() => this.setLoadingState(false), 1000);
                    this.handleResponseError(error);
                }
            );
        },

        changeCategoryOrder(orderData) {
            this.setLoadingState(true);
            this.$load(
                async () => {
                    await this.$api.media.changeCategoryOrder(
                        this.caseId,
                        orderData
                    );
                    await this.getListTagsForGallery();
                    this.debounce(() => this.setLoadingState(false), 1000);
                },
                error => {
                    this.debounce(() => this.setLoadingState(false), 1000);
                    this.handleResponseError(error);
                }
            );
        },

        // GET GALLERY BY TAGS
        async getListGalleryFiles($state, clientId, params) {
            this.setLoadingState(true);
            await this.$load(
                async () => {
                    const res = await this.$api.media.getListGalleryFiles(
                        this.caseId,
                        clientId,
                        params
                    );
                    const body = res.data.body;
                    const content = body.content || [];

                    if (content.length) {
                        if (this.page === 1) {
                            this.displayedMediaList = content;
                        } else {
                            this.displayedMediaList = [...this.displayedMediaList, ...content];
                        }
                        this.page += 1;
                        $state.loaded();
                    } else {
                        $state.complete();
                    }
                    this.debounce(() => this.setLoadingState(false), 500);
                },
                error => {
                    this.debounce(() => this.setLoadingState(false), 500);
                    this.handleResponseError(error);
                    $state.error();
                }
            );
        },

        // TAGS TAB PANEL
        getListTagsByClientId() {
            this.setLoadingState(true);
            this.$load(
                async () => {
                    const res = await this.$api.media.getListTagsByClientId(
                        this.caseId,
                        this.clientId
                    );
                    this.chatTags = (res.data.body || []).map(category => ({
                        ...category,
                        tags: (category.tags || []).map(tag => ({
                            ...tag,
                            isActive: true,
                        })),
                    }));
                    this.debounce(() => this.setLoadingState(false), 1000);
                },
                error => {
                    this.debounce(() => this.setLoadingState(false), 1000);
                    this.handleResponseError(error);
                }
            );
        },

        // TAGS FOR CERTAIN GALLERY
        getListTagsForGallery() {
            this.setLoadingState(true);
            this.$load(
                async () => {
                    const res = await this.$api.media.getListTagsForGallery(
                        this.galleryId,
                        this.caseId
                    );
                    this.galleryTagsList = (res.data.body.categories || []).map(cat => ({
                        ...cat,
                        tags: (cat.tags || []).map(tag => ({
                            ...tag,
                            isActive: tag.isSelected,
                        })),
                    }));
                    this.debounce(() => this.setLoadingState(false), 1000);
                },
                error => {
                    this.debounce(() => this.setLoadingState(false), 1000);
                    this.handleResponseError(error);
                }
            );
        },

        // TAGGING FUNCTIONALITY
        async addTagToGallery(tagId) {
            this.setLoadingState(true);
            await this.$load(
                async () => {
                    await this.$api.media.addTagToGallery(
                        this.caseId,
                        this.galleryId,
                        tagId
                    );
                    this.debounce(() => this.setLoadingState(false), 1000);
                },
                error => {
                    this.debounce(() => this.setLoadingState(false), 1000);
                    this.handleResponseError(error);
                }
            );
        },

        async removeTagFromGallery(tagId) {
            this.setLoadingState(true);
            await this.$load(
                async () => {
                    await this.$api.media.removeTagFromGallery(
                        this.caseId,
                        this.galleryId,
                        tagId
                    );
                    this.debounce(() => this.setLoadingState(false), 1000);
                },
                error => {
                    this.debounce(() => this.setLoadingState(false), 1000);
                    this.handleResponseError(error);
                }
            );
        },

        // COMMON

        // GALLERY ITEMS PAGEABLE
        async getGalleryListFiles($state, clientId, params) {
            this.setLoadingState(true);
            await this.$load(
                async () => {
                    const res = await this.$api.media.getGalleryListFiles(
                        this.caseId,
                        clientId,
                        params
                    );
                    let content = res.data.body.content.map((i) => {
                        return {
                            ...i,
                            // uniqKey need only for normal render, because id from back-end may be not unique
                            uniqKey: this.createUniqKey(i.id),
                            id: i.id,
                            link: i.link,
                            name: i.name,
                            type: i.contentType
                        };
                    });
                    if (content.length) {
                        this.page += 1;

                        const reversedContent = content.reverse().map(item => {
                            const isSelected = this.isCheckedAllMessages || this.selectedAttachmentsIds.includes(item.id);
                            return {...item, isActive: isSelected};
                        });

                        this.displayedMediaList.unshift(...reversedContent);

                        if (this.isCheckedAllMessages) {
                            const newIds = reversedContent.map(item => item.id);
                            this.selectedAttachmentsIds = [...new Set([...this.selectedAttachmentsIds, ...newIds])];
                        }

                        $state.loaded();
                    } else {
                        $state.complete();
                        this.loadFlag = false;
                    }
                    this.debounce(() => this.setLoadingState(false), 1000);
                },
                error => {
                    this.debounce(() => this.setLoadingState(false), 1000);
                    this.handleResponseError(error);
                    $state.error();
                }
            );
        },

        exportGallery(format, idsList) {
            this.setLoadingState(true);
            this.$load(
                async () => {
                    await this.$api.media.exportGallery(
                        this.caseId,
                        format,
                        idsList
                    );
                    this.debounce(() => this.setLoadingState(false), 1000);
                },
                error => {
                    this.debounce(() => this.setLoadingState(false), 1000);
                    this.handleResponseError(error);
                }
            );
        },

        getClientsAndDevicesList(params) {
            this.setLoadingState(true);
            this.$load(
                async () => {
                    const res = await this.$api.media.getClientsAndDevicesList(
                        this.caseId,
                        params
                    );
                    this.debounce(() => this.setLoadingState(false), 1000);
                    return res.data.body;
                },
                error => {
                    this.debounce(() => this.setLoadingState(false), 1000);
                    this.handleResponseError(error);
                }
            );
        },

        // COMMENTS
        async createComment(galleryId, commentRequest) {
            this.setLoadingState(true);
            await this.$load(
                async () => {
                    await this.$api.media.createComment(
                        this.caseId,
                        galleryId,
                        commentRequest
                    );
                    this.debounce(() => this.setLoadingState(false), 1000);
                },
                error => {
                    this.debounce(() => this.setLoadingState(false), 1000);
                    this.handleResponseError(error);
                }
            );
        },

        async editComment(commentId, commentRequest) {
            this.setLoadingState(true);
            await this.$load(
                async () => {
                    await this.$api.media.editComment(
                        this.caseId,
                        commentId,
                        commentRequest
                    );
                    this.debounce(() => this.setLoadingState(false), 1000);
                },
                error => {
                    this.debounce(() => this.setLoadingState(false), 1000);
                    this.handleResponseError(error);
                }
            );
        },

        async deleteComment(commentId) {
            this.setLoadingState(true);
            await this.$load(
                async () => {
                    await this.$api.media.deleteComment(
                        this.caseId,
                        commentId
                    );
                    this.debounce(() => this.setLoadingState(false), 1000);
                },
                error => {
                    this.debounce(() => this.setLoadingState(false), 1000);
                    this.handleResponseError(error);
                }
            );
        },

        async getListComments(galleryId) {
            this.setLoadingState(true);
            await this.$load(
                async () => {
                    const res = await this.$api.media.getListComments(
                        this.caseId,
                        galleryId || this.taggingItem.id,
                    );
                    this.comments = [...res.data.body];
                    this.debounce(() => this.setLoadingState(false), 1000);
                },
                error => {
                    this.debounce(() => this.setLoadingState(false), 1000);
                    this.handleResponseError(error);
                }
            );
        },

        async getListCommentsByClientId($state, clientId, params) {
            this.setLoadingState(true);
            await this.$load(
                async () => {
                    const res = await this.$api.media.getListCommentsByClientId(
                        this.caseId,
                        clientId,
                        params
                    );
                    const body = res.data.body;
                    const content = body.content || [];

                    if (this.page === 1) {
                        this.comments = content;
                    } else {
                        this.comments = [...this.comments, ...content];
                    }

                    if (body.last) {
                        $state.complete();
                    } else {
                        this.page += 1;
                        $state.loaded();
                    }

                    if (this.comments.length > 0 && this.currentIndex === 0) {
                        this.changeActiveComment(this.comments[0].galleryId, 0);
                    }
                    this.debounce(() => this.setLoadingState(false), 1000);
                },
                error => {
                    this.debounce(() => this.setLoadingState(false), 1000);
                    this.handleResponseError(error);
                    $state.error();
                }
            );
        },


        // BLUR
        async blurGallery(galleryId) {
            this.setLoadingState(true);
            await this.$load(
                async () => {
                    await this.$api.media.blurGallery(
                        this.caseId,
                        galleryId
                    );
                    this.debounce(() => this.setLoadingState(false), 1000);
                },
                error => {
                    this.debounce(() => this.setLoadingState(false), 1000);
                    this.handleResponseError(error);
                }
            );
        },

        async unBlurGallery(galleryId) {
            this.setLoadingState(true);
            await this.$load(
                async () => {
                    await this.$api.media.unBlurGallery(
                        this.caseId,
                        galleryId
                    );
                    this.debounce(() => this.setLoadingState(false), 1000);
                },
                error => {
                    this.debounce(() => this.setLoadingState(false), 1000);
                    this.handleResponseError(error);
                }
            );
        },

        async showBluredGallery(galleryId) {
            this.setLoadingState(true);
            await this.$load(
                async () => {
                    const res = await this.$api.media.showBluredGallery(
                        this.caseId,
                        galleryId || this.taggingItem.id
                    );
                    this.unblurredLink = res.data.body.link;

                    // Trigger HEIC conversion if the unblurred file is HEIC format
                    if (this.unblurredLink && this.taggingItem && this.taggingItem.name) {
                        const isHeicFormat = this.isHeicFormat && this.isHeicFormat(this.taggingItem.name);
                        if (isHeicFormat && this.convertImage) {
                            try {
                                const apiContext = {
                                    api: this.$api,
                                    caseId: this.caseId,
                                    galleryId: galleryId || this.taggingItem.id,
                                    clientId: this.clientId
                                };

                                await this.convertImage({
                                    originalUrl: this.unblurredLink,
                                    filename: this.taggingItem.name,
                                    apiContext
                                });
                            } catch (error) {
                                console.error('HEIC conversion failed for unblurred image:', error);
                            }
                        }
                    }

                    this.debounce(() => this.setLoadingState(false), 1000);
                    return res.data.body;
                },
                error => {
                    this.debounce(() => this.setLoadingState(false), 1000);
                    this.handleResponseError(error);
                }
            );
        },


        // PIN
        async pinGallery(galleryIds) {
            this.setLoadingState(true);
            await this.$load(
                async () => {
                    await this.$api.media.pinGallery(this.caseId, galleryIds);
                    this.debounce(() => this.setLoadingState(false), 1000);
                },
                error => {
                    this.debounce(() => this.setLoadingState(false), 1000);
                    this.handleResponseError(error);
                }
            );
        },

        async unPinGallery(galleryIds) {
            this.setLoadingState(true);
            await this.$load(
                async () => {
                    await this.$api.media.unPinGallery(this.caseId, galleryIds);
                    this.debounce(() => this.setLoadingState(false), 1000);
                },
                error => {
                    this.debounce(() => this.setLoadingState(false), 1000);
                    this.handleResponseError(error);
                }
            );
        },
    },
};
